import {
  lazy,
  startTransition,
  Suspense,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type RefObject,
} from 'react'

import {SearchIcon} from '@primer/octicons-react'
import {Button} from '@primer/react-brand/esm'
import {PortalContext, registerPortalRoot} from '@primer/react'

import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {getPrimaryKeybinding} from '@github-ui/ui-commands'
import {GlobalCommands} from '@github-ui/ui-commands/components/GlobalCommands'

import {searchAnalyticsEvent} from '../header-analytics'
import styles from './HeaderSearch.module.css'

// Code-split QuickSearch out of the initial marketing-header chunk. Importing it also fires QuickSearch's
// default-suggestion pre-warm, so we mount only on open (not on hover/mount) to keep that off the initial
// payload for users who never search.
const QuickSearch = lazy(async () => {
  const {QuickSearch: Component} = await import('@github-ui/quick-search')
  return {default: Component}
})

// Reuse the global-navigation search command id so the `/` (and `s`) hotkey opens marketing search. The
// two headers are page-exclusive, and sharing the id dedupes cleanly if that ever breaks.
const SEARCH_COMMAND_ID = 'global-navigation:search'

type HeaderSearchProps = {
  /** Called right before the dialog opens; the composer closes the mobile menu so its focus trap doesn't fight the portalled dialog. */
  onOpen?: () => void
  fallbackFocusRef?: RefObject<HTMLElement | null>
}

/**
 * Marketing-header search trigger; the shared `@github-ui/quick-search` dialog is lazy-loaded on open.
 * Rendered for both logged-out and logged-in users (QuickSearch detects auth internally: real search vs.
 * the explore-link experience). Visibility is owned by {@link MarketingHeader}, so this only renders when
 * the composer mounts it.
 */
export function HeaderSearch({onOpen, fallbackFocusRef}: HeaderSearchProps) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const searchPageQuery = useSyncExternalStore(subscribeToLocation, getSearchPageQuery, getServerSearchPageQuery)
  const [editedQuery, setEditedQuery] = useState<{value: string; sourceQuery: string | null} | null>(null)
  const seededQuery = editedQuery?.value ?? searchPageQuery ?? ''
  const displayedQuery = searchPageQuery === null ? '' : seededQuery.trim()
  // QuickSearch reads its initial query on mount while open. Keep its identity stable once editing begins.
  const quickSearchKey = editedQuery ? editedQuery.sourceQuery : searchPageQuery
  const portalContainerId = useId()
  const portalContextValue = useMemo(() => ({portalContainerName: portalContainerId}), [portalContainerId])

  useEffect(() => {
    const root = document.createElement('div')
    root.className = styles.PortalRoot
    document.body.appendChild(root)
    registerPortalRoot(root, portalContainerId)
    return () => {
      root.remove()
      registerPortalRoot(document.body, portalContainerId)
    }
  }, [portalContainerId])

  const searchKeybinding = getPrimaryKeybinding(SEARCH_COMMAND_ID) || '/'

  // Include the visible label and shortcut in the accessible name (WCAG 2.5.3 Label in Name), matching
  // the global-navigation trigger.
  const triggerLabel = `${displayedQuery || 'Search or jump to'}, type ${searchKeybinding} to search`

  const openSearch = useCallback(() => {
    onOpen?.()
    startTransition(() => setIsOpen(true))
  }, [onOpen])

  const closeSearch = useCallback(() => {
    // Closing the mobile menu hides the search trigger; return to the visible toggle instead.
    returnFocusRef.current = triggerRef.current?.checkVisibility({checkVisibilityCSS: true})
      ? triggerRef.current
      : (fallbackFocusRef?.current ?? triggerRef.current)
    setIsOpen(false)
  }, [fallbackFocusRef])

  // Memoized so the ui-commands registry isn't churned on every render.
  const commands = useMemo(() => ({[SEARCH_COMMAND_ID]: openSearch}), [openSearch])

  function updateQuery(value: string) {
    setEditedQuery(previous => ({value, sourceQuery: previous ? previous.sourceQuery : searchPageQuery}))
  }

  return (
    <div className={styles.searchSlot}>
      <Button
        ref={triggerRef}
        type="button"
        variant="subtle"
        size="small"
        className={styles.trigger}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={triggerLabel}
        onClick={openSearch}
        {...searchAnalyticsEvent}
      >
        <span className={styles.content}>
          <SearchIcon className={styles.icon} aria-hidden="true" />
          <span className={styles.label}>{displayedQuery || 'Search'}</span>
          <kbd className={styles.kbd} aria-hidden="true">
            {searchKeybinding}
          </kbd>
        </span>
      </Button>

      <GlobalCommands commands={commands} />

      {isOpen ? (
        <ErrorBoundary fallback={null} boundaryName="marketing-header-quick-search">
          <Suspense fallback={null}>
            <PortalContext value={portalContextValue}>
              <QuickSearch
                key={quickSearchKey}
                isOpen={isOpen}
                initialQuery={seededQuery}
                onQueryChange={updateQuery}
                onClose={closeSearch}
                returnFocusRef={returnFocusRef}
                anchorRef={triggerRef}
                align="end"
              />
            </PortalContext>
          </Suspense>
        </ErrorBoundary>
      ) : null}
    </div>
  )
}

function subscribeToLocation(onStoreChange: () => void) {
  window.addEventListener('popstate', onStoreChange)
  window.addEventListener('statechange', onStoreChange)
  document.addEventListener('soft-nav:end', onStoreChange)

  return () => {
    window.removeEventListener('popstate', onStoreChange)
    window.removeEventListener('statechange', onStoreChange)
    document.removeEventListener('soft-nav:end', onStoreChange)
  }
}

function getSearchPageQuery() {
  return window.location.pathname === '/search'
    ? (new URLSearchParams(window.location.search).get('q')?.trim() ?? '')
    : null
}

function getServerSearchPageQuery() {
  return null
}
